import { useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useSupabaseQuery } from '@/hooks/useSupabaseQuery';
import { Loader2 } from 'lucide-react';
import { format } from 'date-fns';

interface AuditLogRow {
  id: string;
  action: string;
  entity: string;
  entity_id: string;
  actor_user_id: string | null;
  created_at: string;
  diff_json: Record<string, unknown> | null;
}

const AuditLogPage = () => {
  const [actionFilter, setActionFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');

  const logsQuery = useSupabaseQuery(['audit-logs', actionFilter, entityFilter], async () => {
    if (!supabase) return [] as AuditLogRow[];

    let query = supabase
      .from('audit_logs')
      .select('id, action, entity, entity_id, diff_json, actor_user_id, created_at')
      .order('created_at', { ascending: false })
      .limit(200);

    if (actionFilter !== 'all') {
      query = query.ilike('action', `%${actionFilter}%`);
    }

    if (entityFilter !== 'all') {
      query = query.eq('entity', entityFilter);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data ?? [];
  });

  const availableActions = useMemo(() => {
    const set = new Set<string>();
    (logsQuery.data ?? []).forEach((row) => set.add(row.action));
    return Array.from(set).sort();
  }, [logsQuery.data]);

  const availableEntities = useMemo(() => {
    const set = new Set<string>();
    (logsQuery.data ?? []).forEach((row) => set.add(row.entity));
    return Array.from(set).sort();
  }, [logsQuery.data]);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-slate-100">Audit Log</h1>
        <p className="text-sm text-slate-400">Trace all plan actions including publish, suspend, and catalog revalidation.</p>
      </header>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-800 bg-slate-900/60 p-4 text-sm">
        <div className="flex flex-col">
          <label className="mb-1 text-xs uppercase tracking-wide text-slate-500">Action</label>
          <select
            value={actionFilter}
            onChange={(event) => setActionFilter(event.target.value)}
            className="rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
          >
            <option value="all">All actions</option>
            {availableActions.map((action) => (
              <option key={action} value={action}>
                {action}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col">
          <label className="mb-1 text-xs uppercase tracking-wide text-slate-500">Entity</label>
          <select
            value={entityFilter}
            onChange={(event) => setEntityFilter(event.target.value)}
            className="rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
          >
            <option value="all">All entities</option>
            {availableEntities.map((entity) => (
              <option key={entity} value={entity}>
                {entity}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="rounded-lg border border-slate-800 bg-slate-900/60">
        {logsQuery.isFetching ? (
          <div className="flex h-48 items-center justify-center text-slate-400">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading logs
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-800">
              <thead className="bg-slate-900/80">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">When</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Action</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Entity</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Diff</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {(logsQuery.data ?? []).map((log) => (
                  <tr key={log.id} className="hover:bg-slate-800/40">
                    <td className="px-4 py-3 text-sm text-slate-300">{format(new Date(log.created_at), 'MMM d, yyyy p')}</td>
                    <td className="px-4 py-3 text-sm text-slate-200">{log.action}</td>
                    <td className="px-4 py-3 text-sm text-slate-200">
                      {log.entity}
                      <span className="ml-2 text-xs text-slate-500">{log.entity_id}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-300">
                      <pre className="overflow-x-auto whitespace-pre-wrap rounded bg-slate-950/60 p-3 text-xs text-emerald-200">
                        {JSON.stringify(log.diff_json ?? {}, null, 2)}
                      </pre>
                    </td>
                  </tr>
                ))}
                {!logsQuery.data?.length && (
                  <tr>
                    <td colSpan={4} className="px-4 py-16 text-center text-sm text-slate-400">
                      No audit entries match the selected filters yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogPage;
